<template>
  <div class="explore-time-page">
    <div class="page-title">
      <CalendarIcon/>
      <h1>Explore dates</h1>
    </div>
    <div class="page-content">
      <div class="month-nav">
        <button @click="moveMonth(-1)" class="nav-btn"><ChevronLeftIcon size="16"/></button>
        <div class="month-label-container">
          <Transition :name="monthDirection">
            <div class="month-label" :key="displayMonthText">{{ displayMonthText }}</div>
          </Transition>
        </div>
        <button @click="moveMonth(1)" class="nav-btn"><ChevronRightIcon size="16"/></button>
      </div>
      <div class="day-picker">
        <button @click="moveDay(-1)" class="nav-btn"><ChevronLeftIcon size="18"/></button>

        <div class="day-strip-viewport">
          <div class="day-strip" :style="stripStyle" @transitionend="onSlideEnd">
            <div v-for="day in extendedDays" :key="dayKey(day)"
                 class="day-box"
                 :class="{ today: isToday(day) }"
                 @click="selectDay(day)">
              <span class="day-number" :class="{ 'no-events': !hasEvents(day) }">{{ day.day }}</span>
              <span class="event-dot" :class="{ visible: hasEvents(day) }"></span>
              <span v-if="isToday(day)" class="today-label">TODAY</span>
            </div>
          </div>
          <div class="selection-indicator"></div>
        </div>

        <button @click="moveDay(1)" class="nav-btn"><ChevronRightIcon size="18"/></button>
      </div>

      <!-- Event list -->
      <ScopeLoadProgress v-if="scopeLoading"/>
      <TodayList v-else :items="selectedDayItems"/>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.explore-time-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding-top: 1rem;
}

.page-title {
  display: flex;
  gap: 1rem;
  align-items: center;

  .calendar-icon {
    width: 3rem;
  }
}

.page-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.month-nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.month-label-container {
  position: relative;
  min-width: 6rem;
  overflow: hidden;
}

.month-label {
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: center;
}

// Month slide transitions
.month-left-enter-active,
.month-left-leave-active,
.month-right-enter-active,
.month-right-leave-active {
  transition: all 0.25s ease;
}

.month-left-leave-active,
.month-right-leave-active {
  position: absolute;
  left: 0;
  right: 0;
}

.month-left-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.month-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.month-right-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}
.month-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.day-picker {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
}

.nav-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #666;
  display: flex;
  align-items: center;

  &:hover {
    color: #333;
  }
}

$box-size: 2.5rem;
$box-gap: 0.5rem;
$box-step: $box-size + $box-gap;

.day-strip-viewport {
  position: relative;
  width: calc(7 * #{$box-step} - #{$box-gap});
  clip-path: inset(-0.5rem 0 -1.5rem 0);
}

.day-strip {
  display: flex;
  gap: $box-gap;
}

.day-box {
  width: $box-size;
  height: $box-size;
  min-width: $box-size;
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
  cursor: pointer;
  position: relative;
  background: white;

  &:hover {
    border-color: #999;
  }

  &.today {
    .today-label {
      display: block;
    }
  }
}

.selection-indicator {
  position: absolute;
  top: -2px;
  left: calc(3 * #{$box-step} - 2px);
  width: calc(#{$box-size} + 4px);
  height: calc(#{$box-size} + 4px);
  border: 3px solid #333;
  border-radius: 0.25rem;
  pointer-events: none;
  z-index: 1;
}

.day-number {
  font-size: 1rem;
  font-weight: 500;
  line-height: 1;

  &.no-events {
    color: #888;
  }
}

.event-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: transparent;
  margin-top: 2px;

  &.visible {
    background: var(--brand-primary);
  }
}

.today-label {
  display: none;
  position: absolute;
  bottom: -1.25rem;
  font-size: 0.625rem;
  font-weight: 700;
  color: #888;
  letter-spacing: 0.05em;
  white-space: nowrap;
}
</style>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { DateTime } from 'luxon'
import CalendarIcon from '@/explore/time/CalendarIcon.vue'
import ScopeLoadProgress from '@/explore/ScopeLoadProgress.vue'
import TodayList from '@/explore/time/TodayList.vue'
import { ChevronLeftIcon, ChevronRightIcon } from '@zhuowenli/vue-feather-icons'
import { useExploreStore } from '@/explore/ExploreStore'
import { useViewScopeStore } from '../ViewScopeStore'
import { useAncestorsToday } from './AncestorsToday'
import { TodayItem } from './TodayItem'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { LoadMode } from '@/util/AsyncData'

const exploreStore = useExploreStore()
const viewScopeStore = useViewScopeStore()
const ancestorsToday = useAncestorsToday()
const viewPersonStore = useViewPersonStore()

// Use leap year 2024 as base so Feb 29 is valid
const LEAP_YEAR = 2024
const selectedDay = ref(DateTime.utc(LEAP_YEAR, DateTime.local().month, DateTime.local().day))

// Sliding strip state
const BOX_STEP_REM = 3 // $box-size (2.5) + $box-gap (0.5)
const BASE_OFFSET = -3 * BOX_STEP_REM // -9rem, centers indices 3-9 in viewport
const stripOffset = ref(0)
const animating = ref(false)
let pendingDay: DateTime | null = null

// All events, keyed by "M-D" string
const allEventsMap = ref<Map<string, TodayItem[]>>(new Map())

const scopeLoading = computed(() => !viewScopeStore.getAsyncScopeInfo(exploreStore.startPersonId, LoadMode.EnsureLoaded)?.isLoaded)
const scopeInfoRef = computed(() => viewScopeStore.getAsyncScopeInfo(exploreStore.startPersonId, LoadMode.EnsureLoaded)?.data)

watch(scopeInfoRef, () => {
    if (scopeInfoRef.value) {
        const events = ancestorsToday.getAllAncestorEvents()

        // Add relation paths
        for (const item of events) {
            if (item.personId) {
                item.relationPath = viewPersonStore.getLoadedRelationPath(scopeInfoRef.value.startPersonId, item.personId)
            }
        }

        // Group by month-day
        const map = new Map<string, TodayItem[]>()
        for (const item of events) {
            const key = `${item.date.month}-${item.date.day}`
            if (!map.has(key)) map.set(key, [])
            map.get(key)!.push(item)
        }
        allEventsMap.value = map
    }
}, { immediate: true })

function dayKey(day: DateTime) {
    return `${day.month}-${day.day}`
}

const displayMonthText = ref(selectedDay.value.toFormat('MMMM'))
const monthDirection = ref('')

// 13 days centered on selectedDay (6 buffer on each side for multi-step clicks)
const extendedDays = computed(() => {
    const days: DateTime[] = []
    for (let offset = -6; offset <= 6; offset++) {
        days.push(wrapDay(selectedDay.value.plus({ days: offset })))
    }
    return days
})

const stripStyle = computed(() => ({
    transform: `translateX(${BASE_OFFSET + stripOffset.value}rem)`,
    transition: animating.value ? 'transform 0.25s ease' : 'none'
}))

// Wrap a date to stay within the leap year
function wrapDay(dt: DateTime) {
    if (dt.year > LEAP_YEAR) {
        return DateTime.utc(LEAP_YEAR, 1, 1).plus({ days: dt.diff(DateTime.utc(LEAP_YEAR + 1, 1, 1), 'days').days })
    }
    if (dt.year < LEAP_YEAR) {
        return DateTime.utc(LEAP_YEAR, 12, 31).minus({ days: DateTime.utc(LEAP_YEAR, 1, 1).diff(dt, 'days').days - 1 })
    }
    return dt
}

function isToday(day: DateTime) {
    const now = DateTime.local()
    return day.month === now.month && day.day === now.day
}

function hasEvents(day: DateTime) {
    return (allEventsMap.value.get(dayKey(day))?.length ?? 0) > 0
}

function navigateTo(day: DateTime) {
    if (animating.value) return
    const index = extendedDays.value.findIndex(d => d.month === day.month && d.day === day.day)
    if (index === -1) return
    const offset = index - 6 // distance from center
    if (offset === 0) return

    // Animate month label if crossing a month boundary
    const targetMonth = day.toFormat('MMMM')
    if (targetMonth !== displayMonthText.value) {
        monthDirection.value = offset > 0 ? 'month-left' : 'month-right'
        displayMonthText.value = targetMonth
    }

    pendingDay = day
    animating.value = true
    stripOffset.value = -offset * BOX_STEP_REM
}

function onSlideEnd() {
    if (!pendingDay) return
    animating.value = false
    stripOffset.value = 0
    selectedDay.value = pendingDay
    pendingDay = null
}

function selectDay(day: DateTime) {
    navigateTo(day)
}

function moveDay(offset: number) {
    navigateTo(wrapDay(selectedDay.value.plus({ days: offset })))
}

function moveMonth(offset: number) {
    if (animating.value) return
    animating.value = false
    stripOffset.value = 0
    pendingDay = null
    let target = selectedDay.value.plus({ months: offset })
    // Keep in leap year bounds
    target = DateTime.utc(LEAP_YEAR, target.month, Math.min(target.day, DateTime.utc(LEAP_YEAR, target.month).daysInMonth!))
    selectedDay.value = target
    monthDirection.value = offset > 0 ? 'month-left' : 'month-right'
    displayMonthText.value = target.toFormat('MMMM')
}

// Sort by relationship proximity: direct hops + reverse hops * 2
function getProximityScore(item: TodayItem) {
    if (!item.relationPath) return Infinity
    return item.relationPath.startPersonPath.length + item.relationPath.toPersonPath.length * 2
}

const selectedDayItems = computed(() => {
    const key = dayKey(selectedDay.value)
    const items = allEventsMap.value.get(key) ?? []
    return [...items].sort((a, b) => getProximityScore(a) - getProximityScore(b))
})
</script>
